import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useSnackbar, ScrollableDrawer } from '@emburse/embark-core';
import { useMemo, useState } from 'react';
import { saveAuditDef } from '../../api/audit';
import { $t } from '../../hooks/useTranslation';
import useLoadingOverlay from '../../hooks/useLoadingOverlay';
import { AuditDefParamType } from '../../interfaces/Audit';
import CorpAuditConfigDrawerFooter from './Drawer/Footer';
import CorpAuditConfigDrawerAuditDefinitionDetails from './Drawer/AuditDefinitionDetails';
import CorpAuditConfigDrawerExceptionDetails from './Drawer/ExceptionsDrawer/ExceptionDetails';
import CorpAuditConfigExceptionsDrawerFooter from './Drawer/ExceptionsDrawer/ExceptionsFooter';
var CorpAuditConfigDrawerViewMode;
(function (CorpAuditConfigDrawerViewMode) {
    CorpAuditConfigDrawerViewMode[CorpAuditConfigDrawerViewMode["AUDIT_DEFINITION"] = 0] = "AUDIT_DEFINITION";
    CorpAuditConfigDrawerViewMode[CorpAuditConfigDrawerViewMode["EXCEPTION_DETAILS"] = 1] = "EXCEPTION_DETAILS";
})(CorpAuditConfigDrawerViewMode || (CorpAuditConfigDrawerViewMode = {}));
export default function CorpAuditConfigDrawer({ auditDefinition, drawerIsOpen, globalAuditConfig, setDrawerIsOpen, onSave }) {
    const { enqueueSnackbar } = useSnackbar();
    const { applyLoadingState, removeLoadingState } = useLoadingOverlay();
    const [auditDefinitionPayload, setAuditDefinitionPayload] = useState(null);
    const [viewMode, setViewMode] = useState(CorpAuditConfigDrawerViewMode.AUDIT_DEFINITION);
    const [exceptionDetailType, setExceptionDetailType] = useState(null);
    useMemo(() => {
        if (drawerIsOpen) {
            const paramsWithAppliedDefaults = auditDefinition.Params.map((param) => {
                if ([
                    AuditDefParamType.Currency,
                    AuditDefParamType.Keywords,
                    AuditDefParamType.Percent
                ].includes(param.Type)) {
                    return {
                        ...param,
                        Data: param.Data.length ? param.Data : param.DefaultValue
                    };
                }
                return param;
            });
            setAuditDefinitionPayload({
                Name: auditDefinition.Name,
                Description: auditDefinition.Description,
                AuditDefPK: auditDefinition.AuditDefPK,
                isCompanyActive: auditDefinition.IsCompanyActive,
                ExpenseTypes: auditDefinition.Exceptions.ExpenseTypes.map((expenseType) => expenseType.TypeID),
                UserRoles: auditDefinition.Exceptions.UserRoles.map((userRole) => userRole.TypeID),
                CompanyAuditDefPK: auditDefinition.CompanyAuditDef.CompanyAuditDefPK,
                Params: paramsWithAppliedDefaults
            });
        }
    }, [auditDefinition, drawerIsOpen]);
    return (_jsxs(ScrollableDrawer, { anchor: "right", open: drawerIsOpen, openUpdated: (open) => {
            setDrawerIsOpen(open);
            setViewMode(CorpAuditConfigDrawerViewMode.AUDIT_DEFINITION);
        }, header: $t('Rule Details'), footer: viewMode === CorpAuditConfigDrawerViewMode.AUDIT_DEFINITION ? (_jsx(CorpAuditConfigDrawerFooter, { onCancelClick: () => setDrawerIsOpen(false), onSaveClick: async () => {
                try {
                    applyLoadingState();
                    await saveAuditDef(auditDefinitionPayload);
                    enqueueSnackbar($t('Rule saved successfully'), {
                        variant: 'success'
                    });
                    setDrawerIsOpen(false);
                    await onSave();
                }
                catch (error) {
                    enqueueSnackbar(error.message || $t('Error saving rule'), {
                        variant: 'error'
                    });
                }
                finally {
                    removeLoadingState();
                }
            } })) : (_jsx(CorpAuditConfigExceptionsDrawerFooter, { onBackClick: () => setViewMode(CorpAuditConfigDrawerViewMode.AUDIT_DEFINITION) })), children: [viewMode === CorpAuditConfigDrawerViewMode.AUDIT_DEFINITION && (_jsx(CorpAuditConfigDrawerAuditDefinitionDetails, { auditDefinitionPayload: auditDefinitionPayload, globalAuditConfig: globalAuditConfig, setAuditDefinitionPayload: setAuditDefinitionPayload, setExceptionDetailsView: () => setViewMode(CorpAuditConfigDrawerViewMode.EXCEPTION_DETAILS), setExceptionDetailType: setExceptionDetailType })), viewMode === CorpAuditConfigDrawerViewMode.EXCEPTION_DETAILS && (_jsx(CorpAuditConfigDrawerExceptionDetails, { auditDefPayload: auditDefinitionPayload, exceptionDetailType: exceptionDetailType }))] }));
}
